import { Controller } from "stimulus";

import { initCandidateTags } from "../candidate_tags";
import { initSummernote } from "../summernote";
import { mountLogos } from "../utils/bots_prospects/mount_logo";

type Decision = "accept" | "reject" | "later";

interface QueueItem {
  id: string;
  // null once the queue runs past the rows this page rendered
  row: HTMLElement | null;
}

const SPINNER =
  '<div style="padding:48px 22px;text-align:center"><div class="spinner-border text-primary"></div></div>';

export default class extends Controller {
  static targets = [
    "panel",
    "backdrop",
    "body",
    "index",
    "total",
    "fill",
    "actions",
    "rejectMenu",
    "personalizeBtn",
    "personalizable",
    "personalizeModal",
    "emailList",
    "refetchBtn",
    "done",
    "doneAccepted",
    "doneRejected",
    "doneLater",
  ];

  static values = {
    queueUrl: String,
    cardUrl: String,
    personalizeUrl: String,
    acceptUrl: String,
    rejectUrl: String,
    maybeLater: String,
  };

  declare readonly panelTarget: HTMLElement;
  declare readonly backdropTarget: HTMLElement;
  declare readonly bodyTarget: HTMLElement;
  declare readonly indexTarget: HTMLElement;
  declare readonly totalTarget: HTMLElement;
  declare readonly fillTarget: HTMLElement;
  declare readonly actionsTarget: HTMLElement;
  declare readonly rejectMenuTarget: HTMLElement;
  declare readonly personalizeBtnTarget: HTMLElement;
  declare readonly hasPersonalizableTarget: boolean;
  declare readonly personalizeModalTarget: HTMLElement;
  declare readonly hasEmailListTarget: boolean;
  declare readonly emailListTarget: HTMLElement;
  declare readonly hasRefetchBtnTarget: boolean;
  declare readonly refetchBtnTarget: HTMLElement;
  declare readonly doneTarget: HTMLElement;
  declare readonly doneAcceptedTarget: HTMLElement;
  declare readonly doneRejectedTarget: HTMLElement;
  declare readonly doneLaterTarget: HTMLElement;

  declare readonly queueUrlValue: string;
  declare readonly cardUrlValue: string;
  declare readonly personalizeUrlValue: string;
  declare readonly acceptUrlValue: string;
  declare readonly rejectUrlValue: string;
  declare readonly maybeLaterValue: string;

  private queue: QueueItem[] = [];
  private pos = 0;
  private tally: Record<Decision, number> = { accept: 0, reject: 0, later: 0 };
  private busy = false;
  // a decision landed on a candidate whose row isn't on this page, so the table,
  // pager and counts behind the panel no longer match the server
  private listStale = false;
  // Esc unwinds one layer at a time: personalize modal → reject menu → panel.
  private onKeydown = (e: KeyboardEvent): void => {
    if (e.key !== "Escape") return;
    if (this.personalizeModalTarget.classList.contains("open")) {
      this.closePersonalize();
      return;
    }
    if (!this.panelTarget.classList.contains("open")) return;
    if (this.rejectMenuTarget.classList.contains("open")) {
      this.rejectMenuTarget.classList.remove("open");
      return;
    }
    this.close();
  };
  private onDocumentClick = (e: MouseEvent): void => {
    const t = e.target as HTMLElement;
    if (!t.closest(".rev-reject-wrap")) this.rejectMenuTarget.classList.remove("open");
  };

  connect(): void {
    document.addEventListener("keydown", this.onKeydown);
    document.addEventListener("click", this.onDocumentClick);
  }

  disconnect(): void {
    document.removeEventListener("keydown", this.onKeydown);
    document.removeEventListener("click", this.onDocumentClick);
    this.destroyEditors();
  }

  // ---------- open / close ----------

  async open(): Promise<void> {
    this.pos = 0;
    this.tally = { accept: 0, reject: 0, later: 0 };
    this.listStale = false;
    this.doneTarget.classList.remove("show");
    this.bodyTarget.style.opacity = "1";
    this.bodyTarget.innerHTML = SPINNER;
    this.actionsTarget.style.display = "";
    this.panelTarget.classList.add("open");
    this.panelTarget.setAttribute("aria-hidden", "false");
    this.backdropTarget.classList.add("open");

    await this.buildQueue();
    if (!this.queue.length) {
      this.finish();
      return;
    }
    this.render();
  }

  close(): void {
    this.closePersonalize();
    this.rejectMenuTarget.classList.remove("open");
    this.destroyEditors();
    this.panelTarget.classList.remove("open");
    this.panelTarget.setAttribute("aria-hidden", "true");
    this.backdropTarget.classList.remove("open");

    // Reviewing past this page decided candidates the table never listed; only a
    // reload can put the right rows, pager and tab counts back on screen.
    if (this.listStale) {
      this.listStale = false;
      window.location.reload();
    }
  }

  // The queue is the whole Review list, in list order — not just the rows this
  // page rendered, or review mode would stop at candidate 25 of 400 and announce
  // that everything had been reviewed. Rows are paired up where they exist so the
  // list behind the panel can keep itself in sync.
  private async buildQueue(): Promise<void> {
    const rows = Array.from(document.querySelectorAll<HTMLElement>("tr[data-queued-row]"));
    const rowById = new Map<string, HTMLElement>();
    rows.forEach((tr) => {
      const id = tr.querySelector<HTMLInputElement>("input.bulk-check")?.dataset.id;
      if (id) rowById.set(id, tr);
    });

    const visible = rows.filter((tr) => tr.style.display !== "none");
    const domQueue = (): QueueItem[] =>
      visible
        .map((tr) => ({ id: tr.querySelector<HTMLInputElement>("input.bulk-check")?.dataset.id || "", row: tr }))
        .filter((item) => item.id);

    // A chip or advanced filter is a deliberately narrowed review, so keep to the
    // rows it left visible — those filters only ever see the current page.
    if (visible.length < rows.length) {
      this.queue = domQueue();
      return;
    }

    const ids = await this.fetchQueueIds();
    this.queue = ids.length ? ids.map((id) => ({ id, row: rowById.get(id) || null })) : domQueue();
  }

  private async fetchQueueIds(): Promise<string[]> {
    if (!this.queueUrlValue) return [];

    try {
      const res = await fetch(this.queueUrlValue, {
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      return Array.isArray(data.ids) ? data.ids.map(String) : [];
    } catch (e) {
      return []; // fall back to the rows on this page rather than refuse to open
    }
  }

  // ---------- rendering ----------

  private progress(): void {
    const total = this.queue.length;
    this.indexTarget.textContent = String(Math.min(this.pos + 1, total));
    this.totalTarget.textContent = String(total);
    this.fillTarget.style.width = `${total ? (this.pos / total) * 100 : 0}%`;
  }

  private render(): void {
    const item = this.queue[this.pos];
    if (!item) return;

    this.progress();
    this.destroyEditors();
    this.bodyTarget.innerHTML = SPINNER;
    this.bodyTarget.scrollTop = 0;

    const url = this.cardUrlValue.replace("__ID__", encodeURIComponent(item.id));
    const requestedId = item.id;

    fetch(url, { headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" } })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then((html) => {
        // a decision may have advanced past this candidate mid-flight
        if (this.queue[this.pos]?.id !== requestedId) return;
        this.bodyTarget.innerHTML = html;
        initCandidateTags(this.bodyTarget);
        initSummernote();
        mountLogos();
        this.personalizeBtnTarget.style.display = this.hasPersonalizableTarget ? "" : "none";
      })
      .catch(() => {
        if (this.queue[this.pos]?.id !== requestedId) return;
        this.bodyTarget.innerHTML =
          '<div class="rev-sec"><div class="rev-empty">Failed to load this candidate. Skip to the next one, or close and retry.</div></div>';
      });
  }

  private advance(): void {
    this.rejectMenuTarget.classList.remove("open");
    this.pos++;
    if (this.pos >= this.queue.length) {
      this.finish();
      return;
    }
    this.bodyTarget.style.opacity = "0";
    window.setTimeout(() => {
      this.render();
      this.bodyTarget.style.opacity = "1";
    }, 150);
  }

  private finish(): void {
    this.fillTarget.style.width = "100%";
    this.doneAcceptedTarget.textContent = String(this.tally.accept);
    this.doneRejectedTarget.textContent = String(this.tally.reject);
    this.doneLaterTarget.textContent = String(this.tally.later);
    this.destroyEditors();
    this.bodyTarget.innerHTML = "";
    this.actionsTarget.style.display = "none";
    this.doneTarget.classList.add("show");
  }

  // ---------- decisions ----------

  accept(): void {
    this.decide("accept");
  }

  maybeLater(): void {
    this.decide("later");
  }

  toggleRejectMenu(event: Event): void {
    event.stopPropagation();
    this.rejectMenuTarget.classList.toggle("open");
  }

  reject(event: Event): void {
    event.stopPropagation();
    this.rejectMenuTarget.classList.remove("open");
    const reason = (event.currentTarget as HTMLElement).dataset.reason || "";
    this.decide("reject", reason);
  }

  private async decide(kind: Decision, reason?: string): Promise<void> {
    const item = this.queue[this.pos];
    if (!item || this.busy) return;
    this.busy = true;

    const url = kind === "accept" ? this.acceptUrlValue : this.rejectUrlValue;
    const payload: Record<string, unknown> = { bots_prospect_ids: [item.id] };
    if (kind === "later") payload.reject_reason = this.maybeLaterValue;
    else if (kind === "reject" && reason) payload.reject_reason = reason;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrf(), Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(String(res.status));
      await res.json();
    } catch (e) {
      window.alert("Could not save that decision. Please try again.");
      this.busy = false;
      return;
    }

    this.tally[kind] += 1;
    this.syncList(item, kind);
    this.busy = false;
    this.advance();
  }

  private syncList(item: QueueItem, kind: Decision): void {
    if (item.row) item.row.remove();
    else this.listStale = true;
    if (!document.querySelector(".cand-table input.bulk-check")) this.listStale = true;

    this.bumpBadge("review", -1);
    this.bumpBadge(kind === "accept" ? "accepted" : "rejected", 1);
    this.bumpVisibleTotal(-1);
  }

  private bumpBadge(st: string, delta: number): void {
    const badge = document.querySelector<HTMLElement>(`.subtab .badge[data-tab-badge="${st}"]`);
    if (!badge) return;
    const current = parseInt(badge.textContent || "0", 10) || 0;
    badge.textContent = String(Math.max(0, current + delta));
  }

  // The toolbar total is server-rendered text ("12 candidates"), so restate it as
  // decisions land — the same job candidate_bulk.ts does after a bulk action.
  private bumpVisibleTotal(delta: number): void {
    const el = document.querySelector<HTMLElement>(".cand-count");
    if (!el) return;

    const current = parseInt(el.textContent || "", 10);
    if (Number.isNaN(current)) return;

    const next = Math.max(0, current + delta);
    const singular = el.getAttribute("data-noun-singular") || "candidate";
    const plural = el.getAttribute("data-noun-plural") || "candidates";
    el.textContent = `${next} ${next === 1 ? singular : plural}`;

    // Nothing left to walk, so the button must not reopen onto the done screen.
    const btn = document.querySelector<HTMLButtonElement>("[data-review-mode-open]");
    if (btn) btn.disabled = next === 0;
  }

  // ---------- personalise (its own modal, over the panel) ----------

  openPersonalize(): void {
    const item = this.queue[this.pos];
    if (!item || !this.hasPersonalizableTarget) return;

    this.rejectMenuTarget.classList.remove("open");
    this.personalizeModalTarget.innerHTML = SPINNER;
    this.personalizeModalTarget.classList.add("open");

    const url = this.personalizeUrlValue.replace("__ID__", encodeURIComponent(item.id));
    fetch(url, { headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" } })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then((html) => {
        this.personalizeModalTarget.innerHTML = html;
        initSummernote(); // wire the injected body editor
      })
      .catch(() => {
        this.personalizeModalTarget.innerHTML =
          '<div class="pmail"><div class="pmail-body">Could not load the outreach editor. Close and try again.</div></div>';
      });
  }

  closePersonalize(): void {
    if (!this.personalizeModalTarget.classList.contains("open")) return;
    this.destroyEditorsIn(this.personalizeModalTarget);
    this.personalizeModalTarget.classList.remove("open");
    this.personalizeModalTarget.innerHTML = "";
  }

  // Only a click on the backdrop itself dismisses; clicks inside the card don't.
  backdropClosePersonalize(event: Event): void {
    if (event.target !== this.personalizeModalTarget) return;
    this.closePersonalize();
  }

  // Re-run email discovery for this candidate and refresh the address list.
  async refetchEmail(): Promise<void> {
    if (!this.hasRefetchBtnTarget) return;
    const btn = this.refetchBtnTarget;
    const url = btn.dataset.url;
    if (!url || btn.classList.contains("spin")) return;

    btn.classList.add("spin");
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrf(), Accept: "application/json" },
      });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      this.renderEmails(data.email_addresses || []);
    } catch (e) {
      window.alert("Could not re-fetch the email address. Please try again.");
    } finally {
      window.setTimeout(() => btn.classList.remove("spin"), 550);
    }
  }

  private renderEmails(list: Array<{ email_address: string; best: boolean }>): void {
    if (!this.hasEmailListTarget) return;
    if (!list.length) {
      this.emailListTarget.innerHTML = '<div class="pmail-email-empty">No personal email found yet.</div>';
      return;
    }
    this.emailListTarget.innerHTML = list
      .map((entry) => {
        const addr = this.escape(entry.email_address);
        const best = entry.best ? '<span class="pm-email-best">Best</span>' : "";
        return `<div class="pm-email-row${entry.best ? "" : " pm-email-alt"}"><span class="pm-email-addr">${addr}</span>${best}</div>`;
      })
      .join("");
  }

  // "Save & queue" commits the personalized copy, then accepts — one action.
  async savePersonalize(): Promise<void> {
    if (this.busy) return;
    const form = this.personalizeModalTarget.querySelector<HTMLFormElement>("form.customize-message-form");
    if (!form) return;

    this.busy = true;
    try {
      // summernote edits live in the editor until it writes back to the textarea
      form.querySelectorAll<HTMLTextAreaElement>("textarea.summernote").forEach((ta) => {
        ta.value = $(ta).summernote("code");
      });

      const res = await fetch(form.action, {
        method: form.method && form.method.toLowerCase() !== "get" ? form.method : "POST",
        headers: { "X-CSRF-Token": this.csrf(), Accept: "application/json, text/javascript" },
        body: new FormData(form),
      });
      if (!res.ok) throw new Error(String(res.status));
    } catch (e) {
      window.alert("Could not save the personalized message. Please try again.");
      this.busy = false;
      return;
    }

    this.busy = false;
    this.closePersonalize();
    this.decide("accept");
  }

  // ---------- helpers ----------

  private destroyEditors(): void {
    this.destroyEditorsIn(this.bodyTarget);
  }

  private destroyEditorsIn(root: HTMLElement): void {
    root
      .querySelectorAll<HTMLElement>("[data-note-input], .summernote")
      .forEach((el) => $(el).summernote("destroy"));
  }

  private csrf(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || "";
  }

  private escape(s: string): string {
    const d = document.createElement("div");
    d.textContent = s;
    return d.innerHTML;
  }
}
